import React, { useRef, useCallback, useState } from "react"
import { Container, IconButton } from "@material-ui/core"
import useMediaQuery from "@material-ui/core/useMediaQuery"

import ChevronRightIcon from "@material-ui/icons/ChevronRight"
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"

import { Swiper, SwiperSlide, SwiperRef } from "swiper/react"
import { Pagination } from "swiper"
import { REVIEWS_DEFAULT, Review } from "./data"
import * as styles from "./slider.module.scss"
import { StarIcon } from "./icons/Star"

type SliderProps = {
  orderNum: string
  data?: Review[]
  headTitle: string | JSX.Element
}

const sliderParams = {
  breakpoints: {
    1280: {
      spaceBetween: 24,
      slidesPerView: 3,
      slidesPerGroup: 3,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
    },
    0: {
      spaceBetween: 16,
      slidesPerView: "auto" as "auto",
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16,
    },
  },
}

export const SliderReviews = ({ orderNum, data = REVIEWS_DEFAULT, headTitle }: SliderProps) => {
  const isDesktop = useMediaQuery("(min-width:959.5px)")
  const sliderRef = useRef<SwiperRef>(null)

  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const handleSlideChange = useCallback(() => {
    if (!sliderRef.current) return
    const { swiper } = sliderRef.current
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }, [])

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return
    sliderRef.current.swiper.slidePrev()
  }, [])

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return
    sliderRef.current.swiper.slideNext()
  }, [])

  return (
    <section id="reviews" data-exclude={orderNum}>
      <Container className={styles.container}>
        <div className={styles.headWrapper}>
          {headTitle && <h2 className={styles.headTitle}>{headTitle}</h2>}
          {isDesktop && (
            <div className={styles.arrowGroup}>
              <IconButton
                className={styles.arrow}
                onClick={() => handlePrev()}
                disabled={isBeginning}
              >
                <ChevronLeftIcon className={styles.angle} />
              </IconButton>

              <IconButton className={styles.arrow} onClick={() => handleNext()} disabled={isEnd}>
                <ChevronRightIcon className={styles.angle} />
              </IconButton>
            </div>
          )}
        </div>
      </Container>

      <Swiper
        {...sliderParams}
        modules={[Pagination]}
        pagination={{
          clickable: true,
          dynamicBullets: false,
        }}
        ref={sliderRef}
        className={styles.reviewsSwiper}
        onSlideChange={handleSlideChange}
        onInit={handleSlideChange}
      >
        {data.map(({ name, rating, title, description, footerText }, index) => (
          <SwiperSlide key={index} style={{ width: "auto" }}>
            <div className={styles.card}>
              <div>
                <div className={styles.cardHead}>
                  <p className={styles.name}>{name}</p>
                  <div className={styles.rating}>
                    {rating} <StarIcon />
                  </div>
                </div>
                <p className={styles.title}>{title}</p>
                <p className={styles.description}>{description}</p>
              </div>
              <div className={styles.footer}>{footerText}</div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}
